/** @jsx jsx */
import { jsx } from '@emotion/react'
import { css } from '@emotion/react/macro'

import { Modal, ModalHeader, ModalBody, ModalFooter } from '../../shared/modal'
import { SecondaryButton } from '../../shared/buttons'
import { ModalSize } from '../../shared/modal/Modal/Modal'

type AcceptableUseModalProps = {
  /** Determines visibility of modal */
  isOpen: boolean
  /** Event callback when user triggers a close event (eg. button click or ESC key) */
  onCancel?: () => void
}

const defaultProps: Pick<AcceptableUseModalProps, 'onCancel'> = {
  onCancel: undefined
}

const textCss = css`
  height: 500px;
  overflow: scroll;

  li {
    padding: 8px;
  }
`
const headingCss = css`
  text-align: center;
  text-decoration: underline;
`
const lastUpdatedCss = css`
  text-align: center;
`

const AcceptableUseModal = ({ isOpen, onCancel }: AcceptableUseModalProps) => (
  <Modal isOpen={isOpen} size={ModalSize.LG}>
    <ModalHeader onClose={onCancel} title="Acceptable Use Agreement" />
    <ModalBody>
      <div css={textCss}>
        <p css={headingCss}>
          <strong>Civitas Acceptable Use Policy</strong>
        </p>

        <p css={lastUpdatedCss}>Last Updated: August 15, 2020</p>

        <p>
          The use of Civitas Services is subject to this Acceptable Use Policy (“AUP”). This AUP
          describes prohibited uses of the Services. Civitas may modify this AUP at any time by
          posting a revised version on the Civitas website. By using the Services, you agree to the
          latest version of this AUP. If you violate this AUP or authorize or help others to do so,
          we may suspend or terminate your use of the Services.
        </p>

        <p>
          The &quot;<strong>Services</strong>&quot; as that term is used in this AUP includes, but
          is not limited to, the Civitas website, software applications, product information,
          training programs, webinars, documentation, messages generated by the software, and any
          other related content.
        </p>

        <p>By using the Services, you agree to:</p>
        <ul>
          <li>
            Comply with all applicable User Terms, the terms of this AUP and the Privacy Policy.
          </li>
          <li>
            Comply with all applicable laws including, but not limited to, all international,
            federal, state and local laws, statutes, regulations, rules, executive orders,
            supervisory requirements, permitting or licensing requirements, export requirements,
            directives, circulars, opinions, decrees, interpretive letters, guidance or other
            official releases of or by any government, any authority, department or agency thereof,
            or any regulatory or self-regulatory organization.
          </li>
          <li>
            Be responsible for your use of and communications and content you upload, post to, or
            transmit through the Services.
          </li>
          <li>
            Upload, post to, or transmit through the Services only content and data that does not
            violate the intellectual property rights or other rights of third parties, including but
            not limited to trademark, copyright or rights of publicity.
          </li>
          <li>
            Use commercially reasonable efforts to prevent unauthorized access to or use of the
            Services.
          </li>
          <li>Keep passwords and other log-in information confidential.</li>
          <li>
            Promptly notify Civitas if you become aware of or reasonably suspect any illegal or
            unauthorized activity or a security breach involving your account, including any loss,
            theft, or unauthorized disclosure or use of log-in information.
          </li>
        </ul>
        <p>By using the Services, you agree NOT to:</p>
        <ul>
          <li>
            Use the Services for any unlawful purpose or for a purpose that is in violation of this
            AUP, the Privacy Policy, or any applicable User Terms.
          </li>
          <li>
            Upload, post to or transmit through the Services any unlawful, infringing, fraudulent,
            intentionally misleading, threatening, harassing, defamatory, vulgar, obscene, profane,
            indecent, offensive, discriminatory, hateful, harmful, or otherwise objectionable
            material of any kind, including any material that encourages criminal conduct or conduct
            that would give rise to civil liability, infringes others’ intellectual property rights
            or otherwise violates any applicable local, state, national, or international law.
          </li>
          <li>Permit any third party to use your username or password to access the Services.</li>
          <li>
            Use the Services in any manner that interferes with the normal operation of the Services
            or with any other user’s use or enjoyment of the Services (as determined by Civitas in
            its sole discretion) including, but not limited to:
            <ul>
              <li>
                uploading to, or transmitting from, the Services any data, file, software, or link
                that contains or redirects to a virus, Trojan horse, worm, or other harmful
                component or a technology that unlawfully accesses or downloads content or
                information stored within the Services or on the hardware of Civitas or any third
                party;
              </li>
              <li>
                accessing, tampering with, or using non-public areas of the Services, Civitas
                computer systems and technical delivery systems;
              </li>
              <li>
                use any device, software, routine, or take any other action that may interfere with
                the working of the Services or any Civitas system;
              </li>
              <li>
                monitoring or crawling of the Services or Civitas systems in a manner that impairs
                or disrupts the Services or systems; or
              </li>
              <li>
                probing, scanning, or testing the vulnerability of any system or network or breach
                or circumventing any security or authentication measures or system restrictions.
              </li>
            </ul>
          </li>
          <li>
            Use any robot, spider, or other automatic device, process, or means to access the
            Services for any purpose including, but not limited to, monitoring or copying any data,
            information, or content from the Services or Civitas system.
          </li>
          <li>
            Attempt to reverse engineer, decompile, hack, disable, interfere with, disassemble,
            modify, copy, translate, or disrupt the features, functionality, integrity or
            performance of the Services (including any mechanism used to restrict or control the
            functionality of the Service).
          </li>
          <li>
            Access the Services in order to build a similar or competitive product or service or
            copy any content, ideas, features, functions, or user interfaces of the Services.
          </li>
          <li>
            Use the Services in any manner that may harm minors or that interacts with or targets
            people under the age of thirteen.
          </li>
          <li>
            Use the Services to send unsolicited communications, promotions, advertisements, or spam
            or send altered, deceptive, or false source-identifying information, including
            “spoofing” or “phishing.”
          </li>
          <li>Sublicense, resell, time share or similarly exploit the Services.</li>
          <li>
            Use contact information or other user information obtained from the Services (including,
            but not limited to, email addresses) to contact users outside of the Services without
            their permission or authority or to create or distribute mailing lists or other
            collections of contact or user information for users outside of the Services.
          </li>
          <li>
            Impersonate Civitas, a Civitas employee, another use, or any other person or entity
            (including, without limitation, an email address associated with the foregoing).
          </li>
        </ul>
        <p>
          Civitas reserves the right, but does not assume the obligation, to investigate any
          violation of this AUP or misuse of the Services. Civitas may remove, disable access to, or
          modify any content or resource that violates this AUP or any other agreement we have with
          you for the use of the Services, or otherwise violates the spirit and intent of this AUP
          or any other Civitas agreement, rule, policy, or guidance. Civitas may report any activity
          that we suspect violates any law or regulation to appropriate law enforcement officials,
          regulators, or other appropriate third parties. Our reporting may include disclosing
          appropriate user information. Where legally obligated to do so, Civitas shall cooperate
          with any law enforcement or regulatory investigation and/or proceeding relating to your
          use of or access to the Services.
        </p>
        <p>
          By using the Services, you agree that, in the event of any violation of the foregoing,
          Civitas may immediately suspend or terminate your access to the Services without advanced
          notice or liability.
        </p>
        <p>
          If you have any questions about this Acceptable Use Policy, you may contact us at{' '}
          <a href="mailto:legal@civitaslearning.com">legal@civitaslearning.com</a>.
        </p>
      </div>
    </ModalBody>
    <ModalFooter>
      <SecondaryButton onClick={onCancel}>Ok</SecondaryButton>
    </ModalFooter>
  </Modal>
)

AcceptableUseModal.defaultProps = defaultProps

export default AcceptableUseModal
